import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, TrendingUp, Target, Calendar, CheckCircle, ArrowRight, Star, ChartBar } from "lucide-react";
import { Button } from "../components/ui/button";
import { API } from "../App";
import axios from "axios";
import SEO from "../components/SEO";

const RoadTo100KPage = () => {
  const [trackRecord, setTrackRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/track-record`);
        setTrackRecord(res.data);
      } catch (error) {
        console.error("Failed to fetch track record:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, []);

  const FINAL_PROFIT = 102857.52;
  const GOAL = 100000;
  const COMPLETION_DATE = "May 23, 2026";

  return (
    <>
      <SEO 
        title="Road to $100K - Challenge Complete! | HSL.bet"
        description="We did it! HSL.bet completed the Road to $100K challenge with $102,857.52 in verified profit. See our complete track record and results."
      />
      
      <div className="min-h-screen bg-hsl-bg">
        {/* Hero Section - Celebration */}
        <section className="relative pt-24 pb-16 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-green-500/5 via-hsl-bg to-hsl-bg" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl" />
          
          <div className="relative max-w-6xl mx-auto px-4">
            {/* Completed Badge */}
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.8 }}
              className="flex justify-center mb-8"
            >
              <div className="inline-flex items-center gap-2 bg-green-500/20 border border-green-500/40 rounded-full px-6 py-3">
                <CheckCircle className="w-6 h-6 text-green-400" />
                <span className="text-green-400 font-bold text-lg uppercase tracking-wide">Challenge Complete!</span>
              </div>
            </motion.div>

            {/* Main Title */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center"
            >
              <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black uppercase mb-6">
                <span className="text-white">The Road to </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-yellow-400 to-green-400">
                  $100K
                </span>
              </h1>
              
              <p className="text-xl sm:text-2xl text-zinc-400 max-w-3xl mx-auto mb-8">
                From zero to six figures in verified profit. Every pick documented. Every dollar tracked.
              </p>
            </motion.div>

            {/* Final Profit Display */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="bg-gradient-to-br from-green-500/20 via-hsl-surface to-green-500/10 rounded-3xl border border-green-500/30 p-8 md:p-12 mb-12 max-w-4xl mx-auto"
            >
              <div className="text-center">
                <p className="text-zinc-400 uppercase tracking-widest mb-2">Final Profit</p>
                <div className="flex items-center justify-center gap-4 mb-4">
                  <Trophy className="w-12 h-12 text-yellow-400" />
                  <span className="font-mono text-5xl sm:text-6xl md:text-7xl font-black text-green-400">
                    +${FINAL_PROFIT.toLocaleString()}
                  </span>
                  <Trophy className="w-12 h-12 text-yellow-400" />
                </div>
                <p className="text-zinc-500">
                  Goal Reached: <span className="text-white font-semibold">{COMPLETION_DATE}</span>
                </p>
              </div>

              {/* Progress Bar - Completed */}
              <div className="mt-8">
                <div className="relative h-6 bg-hsl-bg rounded-full overflow-hidden border border-white/10">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 2, delay: 0.6 }}
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-500 via-green-400 to-yellow-400 rounded-full"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-white font-bold text-sm drop-shadow-lg">102.9% COMPLETE</span>
                  </div>
                </div>
                
                {/* Milestones */}
                <div className="flex justify-between mt-3 text-sm">
                  {[25000, 50000, 75000, 100000].map((milestone) => (
                    <div key={milestone} className="flex flex-col items-center">
                      <CheckCircle className="w-4 h-4 text-green-400 mb-1" />
                      <span className="text-green-400 font-medium">${(milestone/1000)}K</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-hsl-surface/30">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-center text-white uppercase mb-12">
              The Final Numbers
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: "Total Picks", value: trackRecord?.total_picks || "350+", icon: Target },
                { label: "Win Rate", value: `${trackRecord?.win_rate || 60}%`, icon: TrendingUp },
                { label: "Total Profit", value: `+$${FINAL_PROFIT.toLocaleString()}`, icon: Trophy, highlight: true },
                { label: "ROI", value: `${trackRecord?.roi || 15}%`, icon: ChartBar },
              ].map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className={`p-6 rounded-xl border ${stat.highlight ? 'bg-green-500/10 border-green-500/30' : 'bg-hsl-surface border-white/10'} text-center`}
                >
                  <stat.icon className={`w-8 h-8 mx-auto mb-3 ${stat.highlight ? 'text-green-400' : 'text-hsl-primary'}`} />
                  <p className={`font-mono text-2xl md:text-3xl font-bold ${stat.highlight ? 'text-green-400' : 'text-white'}`}>
                    {stat.value}
                  </p>
                  <p className="text-zinc-500 text-sm uppercase tracking-wide mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </div>

            {/* Tier Breakdown */}
            {trackRecord?.tier_breakdown && (
              <div className="mt-12 grid md:grid-cols-3 gap-6">
                {trackRecord.tier_breakdown.hammer && (
                  <div className="bg-gradient-to-br from-yellow-500/20 to-hsl-surface p-6 rounded-xl border border-yellow-500/30">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-2xl">🔨</span>
                      <h3 className="font-heading text-xl font-bold text-yellow-400 uppercase">Hammer Picks</h3>
                    </div>
                    <div className="space-y-2">
                      <p className="text-white">Record: <span className="font-mono font-bold">{trackRecord.tier_breakdown.hammer.wins}-{trackRecord.tier_breakdown.hammer.losses}</span></p>
                      <p className="text-white">Win Rate: <span className="font-mono font-bold text-yellow-400">{trackRecord.tier_breakdown.hammer.win_rate}%</span></p>
                      <p className="text-white">Profit: <span className={`font-mono font-bold ${trackRecord.tier_breakdown.hammer.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {trackRecord.tier_breakdown.hammer.profit >= 0 ? '+' : ''}${trackRecord.tier_breakdown.hammer.profit.toLocaleString()}
                      </span></p>
                    </div>
                  </div>
                )}
                
                {trackRecord.tier_breakdown.premium && (
                  <div className="bg-gradient-to-br from-hsl-primary/20 to-hsl-surface p-6 rounded-xl border border-hsl-primary/30">
                    <div className="flex items-center gap-2 mb-4">
                      <Star className="w-6 h-6 text-hsl-primary" />
                      <h3 className="font-heading text-xl font-bold text-hsl-primary uppercase">Members Only</h3>
                    </div>
                    <div className="space-y-2">
                      <p className="text-white">Record: <span className="font-mono font-bold">{trackRecord.tier_breakdown.premium.wins}-{trackRecord.tier_breakdown.premium.losses}</span></p>
                      <p className="text-white">Win Rate: <span className="font-mono font-bold text-hsl-primary">{trackRecord.tier_breakdown.premium.win_rate}%</span></p>
                      <p className="text-white">Profit: <span className={`font-mono font-bold ${trackRecord.tier_breakdown.premium.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {trackRecord.tier_breakdown.premium.profit >= 0 ? '+' : ''}${trackRecord.tier_breakdown.premium.profit.toLocaleString()}
                      </span></p>
                    </div>
                  </div>
                )}
                
                {trackRecord.tier_breakdown.free && (
                  <div className="bg-gradient-to-br from-zinc-500/20 to-hsl-surface p-6 rounded-xl border border-zinc-500/30">
                    <div className="flex items-center gap-2 mb-4">
                      <CheckCircle className="w-6 h-6 text-zinc-400" />
                      <h3 className="font-heading text-xl font-bold text-zinc-400 uppercase">Free Picks</h3>
                    </div>
                    <div className="space-y-2">
                      <p className="text-white">Record: <span className="font-mono font-bold">{trackRecord.tier_breakdown.free.wins}-{trackRecord.tier_breakdown.free.losses}</span></p>
                      <p className="text-white">Win Rate: <span className="font-mono font-bold text-zinc-400">{trackRecord.tier_breakdown.free.win_rate}%</span></p>
                      <p className="text-white">Profit: <span className={`font-mono font-bold ${trackRecord.tier_breakdown.free.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {trackRecord.tier_breakdown.free.profit >= 0 ? '+' : ''}${trackRecord.tier_breakdown.free.profit.toLocaleString()}
                      </span></p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* The Story Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-center text-white uppercase mb-12">
              The Story Behind the Challenge
            </h2>
            
            <div className="prose prose-invert prose-lg max-w-none">
              <div className="bg-hsl-surface rounded-xl border border-white/10 p-8 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-hsl-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 text-hsl-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">The Challenge</h3>
                    <p className="text-zinc-400">
                      We set out to prove that sharp, data-driven sports analysis could generate consistent profits. 
                      The goal was ambitious: reach $100,000 in verified profit with complete transparency. 
                      Every pick would be documented before the game. Every result tracked publicly.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">The Method</h3>
                    <p className="text-zinc-400">
                      Our Situational Edge system identifies high-value opportunities across all major sports. 
                      We analyze fatigue factors, travel burden, psychological edges, and personnel situations 
                      to find spots where the line doesn't fully account for real-world variables.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">The Result</h3>
                    <p className="text-zinc-400">
                      On <span className="text-white font-semibold">{COMPLETION_DATE}</span>, we crossed the finish line 
                      with <span className="text-green-400 font-bold">+${FINAL_PROFIT.toLocaleString()}</span> in verified profit. 
                      The Road to $100K is complete, but our winning methodology continues.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-b from-hsl-surface/50 to-hsl-bg">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase mb-4">
              Ready to Join the Winners?
            </h2>
            <p className="text-xl text-zinc-400 mb-8">
              Now that the challenge is complete, get access to the same sharp picks that generated $100K+ in profit.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pricing">
                <Button className="btn-primary-glow text-lg px-8 py-6">
                  Start Your Free Trial <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/track-record">
                <Button variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/10 text-lg px-8 py-6">
                  View Full Results
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default RoadTo100KPage;
